import axios from "axios";
import { toast } from "react-toastify";
import { retrieveToken } from "../utils/cryptoStorage";

const API_URL = import.meta.env.VITE_API_URL_NEWS;

// Axios instance configuration
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Authorization token dynamically
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = retrieveToken("accessToken"); // Fetch token dynamically for each request
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Optionally, you can handle the absence of the token more gracefully
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 403 errors and network errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 || error.response?.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Centralized error handling (excluding 403 and network errors)
const handleError = (error) => {
  if (error.isAxiosError && error.response) {
    const { statusCode, message } = error.response.data;

    if (message) {
      toast.error(message);
    }
    switch (statusCode) {
      case 500:
        throw new Error(
          "Server Error (500): The server encountered an issue. Please try again later."
        );
      case 422:
        throw new Error(
          "Validation Error (422): Invalid or incomplete data provided."
        );
      case 401:
        localStorage.clear();
        window.location.href = "/login";
        throw new Error(
          "Unauthorized (401): Access is denied. Please check your login status."
        );
      default:
        throw new Error(`API Error: Received unexpected status code ${status}`);
    }
  } else {
    throw new Error(error.message || "An unexpected error occurred.");
  }
};

// API methods for category management
export const categoryAdding = async (categoryData) => {
  try {
    const response = await axiosInstance.post(
      "news-admin/category-admin/v1",
      categoryData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const categoryUpdate = async (categoryData) => {
  try {
    const response = await axiosInstance.put(
      "news-admin/category-admin/v1",
      categoryData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const categoryList = async () => {
  try {
    const response = await axiosInstance.get(`news-admin/category-admin/v1`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const categoryDelete = async (categoryData) => {
  try {
    const response = await axiosInstance.delete(
      `news-admin/category-admin/v1`,
      {
        data: {
          news_category_id: [categoryData],
        },
      }
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// API methods for news management
export const newsAdding = async (newsData) => {
  try {
    const response = await axiosInstance.post(`news-admin/v1`, newsData);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const newsUpdate = async (newsData) => {
  try {
    const response = await axiosInstance.put(`news-admin/v1`, newsData);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const newsImageUpdate = async (newsData) => {
  try {
    const response = await axiosInstance.put(`news-admin/image/v1`, newsData);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const newsStatusUpdate = async (newsData) => {
  try {
    const { data } = await axiosInstance.post(`news-admin/status/v1`, newsData);
    return data;
  } catch (error) {
    handleError(error);
  }
};

export const newsDelete = async (newsData) => {
  try {
    const response = await axiosInstance.delete(`news-admin/v1`, {
      data: newsData,
    });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const newsListing = async (
  page = 1,
  perPage = 10,
  status = 1,
  userId,
  title,
  publishedDate,
  expiryDate,
  author,
  categoryId
) => {
  try {
    let url = `news-admin/v1?page=${page}&perPage=${perPage}&status=${status}&isMyNewsArticle=1&createdUserId=${userId}`;

    if (title?.length > 0) url += `&title=${title}`;
    if (author?.length > 0) url += `&authorName=${author}`;
    if (categoryId) url += `&newsCategoryId=${categoryId}`;
    if (publishedDate) url += `&publishedDate=${publishedDate}`;
    if (expiryDate) url += `&expiryDate=${expiryDate}`;

    const response = await axiosInstance.get(url);
    return response.data.data;
  } catch (error) {
    handleError(error);
  }
};

export const newsListingByid = async (
  page = 1,
  perPage = 10,
  wardCode,
  isMyNewsArticle
) => {
  try {
    let url = `news-admin/v1?page=${page}&perPage=${perPage}&isMyNewsArticle=${isMyNewsArticle}&wardCode=${wardCode}`;
    const response = await axiosInstance.get(url);
    return response.data.data;
  } catch (error) {
    handleError(error);
  }
};

export const uploadImageToS3 = async (file, url) => {
  try {
    const response = await axios.put(url, file);
    return response.status;
  } catch (error) {
    throw error;
  }
};
